import CTNButton from '@ctn/ui/components/ctn-button';
import CTNEllipsis from '@ctn/ui/components/ctn-ellipsis';
import React from 'react';

import { ESTADO_COLOR, ESTADOS_FIRMA } from '@/constants/tramite.constants';
import { type IRepresentante, type ISujetoListado, type ITramiteListado } from '@/interfaces/model/don.interface';
import { type ILookup } from '@/interfaces/model/generic.interface';
import { getFullName } from '@/utils/string/string';
import type { CustomColumnDef, RenderSubComponentProps } from '@ctn/ui/types/ctn-table';

import TextIcon from '../TextIcon';

const setColumn = <K extends keyof ITramiteListado>(
	key: K,
	header: string,
	width?: number,
	customCell?: CustomColumnDef<ITramiteListado>['cell']
): CustomColumnDef<ITramiteListado> => ({
	accessorKey: key as string,
	header,
	enableSorting: false,
	...(width ? { th: { width } } : {}),
	responsive: { sizeColumn: 'full' },
	cell: customCell ?? (({ getValue }) => <CTNEllipsis content={getValue() as string} />),
});

const getColumns = ({
	goToDetail,
	tipoTramite = [],
	estadoRevisionFirma
}: {
	goToDetail: (value: ITramiteListado) => void;
	tipoTramite: ILookup[];
	estadoRevisionFirma: ILookup[];
}) => {
	const columns: Array<CustomColumnDef<ITramiteListado>> = [
		setColumn('numeroTramite', 'Nº solicitud', 164),
		setColumn('fechaCreacion', 'Fecha', 164),
		setColumn('estadoTramite', 'Estado', undefined, ({ getValue }) => {
			const value = getValue() as ILookup;
			return (
				<TextIcon
					isFit
					iconLeft={{ color: ESTADO_COLOR[value?.id as keyof typeof ESTADO_COLOR], icon: 'status' }}
					description={value?.descripcion}
				/>
			);
		}),
		setColumn('estadoUltimaSubsanacionFirma', 'Revisión de escritura', undefined, ({ getValue }) => {
			const value = getValue() as ILookup;
			if (!value || value.id === ESTADOS_FIRMA.CANCELADA) return <span>-</span>;
			return (
				<TextIcon
					isFit
					iconLeft={{ color: ESTADO_COLOR[value?.id as keyof typeof ESTADO_COLOR], icon: 'status' }}
					description={estadoRevisionFirma.find((it) => it.id === value?.id)?.descripcion}
				/>
			);
		}),
		setColumn('nombreNotario', 'Notario asignado'),
		setColumn('tipoNNJJ', 'Tipo de negocio jurídico', undefined, ({ getValue }) => {
			const value = getValue() as ITramiteListado['tipoNNJJ'];
			return (
				<CTNEllipsis
					content={value ? tipoTramite.find((data) => data?.id === value.id)?.descripcion : ''}
				/>
			);
		}),
		{
			accessorKey: 'actions',
			header: '',
			th: {
				width: 110,
			},
			td: {
				align: 'center',
			},
			enableSorting: false,
			responsive: { sizeColumn: 'full' },
			cell({ row }) {
				return (
					<CTNButton
						variant="ghost"
						mode="secondary"
						size="size-32"
						label="Ver detalle"
						onClick={() => goToDetail(row.original)}
					/>
				);
			},
		},
	];

	return columns;
};

export default getColumns;

const makeStringRow = ({ numeroDocumento, nombre, apellido1RacSoc, apellido2 }: ISujetoListado | IRepresentante) =>
	`${numeroDocumento} - ${getFullName(nombre, apellido1RacSoc, apellido2)}`;

export const renderSubComponent = ({
	row: {
		original: { sujetos },
	},
}: RenderSubComponentProps<ITramiteListado>) => (
	<ul className="[ o-fade-in ] [ c-pnp-table__subcell ] [ u-flow ]">
		{sujetos.map((sujeto) => {
			const { representantes, numeroDocumento, nombre, apellido1RacSoc, apellido2 } = sujeto;
			const nombreSujeto = getFullName(nombre, apellido1RacSoc, apellido2);

			if (representantes && representantes.length > 0) {
				return (
					<React.Fragment key={numeroDocumento}>
						{representantes.map((rep) => (
							<li key={`${numeroDocumento}-${rep.numeroDocumento}`}>
								{`${makeStringRow(rep)} (en representación de ${nombreSujeto} - ${numeroDocumento})`}
							</li>
						))}
					</React.Fragment>
				);
			}

			return <li key={numeroDocumento}>{makeStringRow(sujeto)}</li>;
		})}
	</ul>
);